import { makeStyles } from "@material-ui/styles";
import { isFailedTask } from "../../utils/helpers";
import { DropdownButton } from "../../components";
import { ListItemIcon, ListItemText } from "@material-ui/core";
import StopIcon from "@material-ui/icons/Stop";
import PauseIcon from "@material-ui/icons/Pause";
import ReplayIcon from "@material-ui/icons/Replay";
import ResumeIcon from "@material-ui/icons/PlayArrow";
import RedoIcon from "@material-ui/icons/Redo";
import FlareIcon from "@material-ui/icons/Flare";

import {
  useRestartAction,
  useRestartLatestAction,
  useResumeAction,
  useRetryResumeSubworkflowTasksAction,
  useRetryAction,
  useTerminateAction,
  usePauseAction,
  useLatestDefinitionsRetryAction,
} from "../../data/actions";

const useStyles = makeStyles({
  terminate: {
    color: "red",
  },
});

export default function ActionModule({ execution, triggerReload, onError }) {
  const classes = useStyles();
  const { workflowId, workflowDefinition } = execution;

  function onSuccess() {
    triggerReload();
  }

  const restartAction = useRestartAction({ workflowId, onSuccess, onError });
  const restartLatestAction = useRestartLatestAction({
    workflowId,
    onSuccess,
    onError,
  });
  const retryAction = useRetryAction({ workflowId, onSuccess, onError });
  const retryResumeSubworkflowTasksAction =
    useRetryResumeSubworkflowTasksAction({ workflowId, onSuccess, onError });
  const retryLatestDefinitionsAction = useLatestDefinitionsRetryAction({
    workflowId,
    onSuccess,
    onError,
  });
  const terminateAction = useTerminateAction({
    workflowId,
    onSuccess,
    onError,
  });
  const resumeAction = useResumeAction({ workflowId, onSuccess, onError });
  const pauseAction = usePauseAction({ workflowId, onSuccess, onError });

  const { restartable } = workflowDefinition;

  const options = [];

  // RESTART buttons
  if (
    ["COMPLETED", "FAILED", "TIMED_OUT", "TERMINATED"].includes(
      execution.status
    ) &&
    restartable
  ) {
    options.push({
      label: (
        <>
          <ListItemIcon>
            <ReplayIcon />
          </ListItemIcon>
          <ListItemText>Restart with Current Definitions</ListItemText>
        </>
      ),
      handler: () => restartAction.mutate(),
    });

    options.push({
      label: (
        <>
          <ListItemIcon>
            <FlareIcon />
          </ListItemIcon>
          <ListItemText>Restart with Latest Definitions</ListItemText>
        </>
      ),
      handler: () => restartLatestAction.mutate(),
    });
  }

  // PAUSE button
  if (execution.status === "RUNNING") {
    options.push({
      label: (
        <>
          <ListItemIcon>
            <PauseIcon />
          </ListItemIcon>
          <ListItemText>Pause</ListItemText>
        </>
      ),
      handler: () => pauseAction.mutate(),
    });
  }

  // RESUME button
  if (execution.status === "PAUSED") {
    options.push({
      label: (
        <>
          <ListItemIcon>
            <ResumeIcon />
          </ListItemIcon>
          <ListItemText>Resume</ListItemText>
        </>
      ),
      handler: () => resumeAction.mutate(),
    });
  }

  // RETRY (from task) button
  if (["FAILED", "TIMED_OUT", "TERMINATED"].includes(execution.status)) {
    options.push({
      label: (
        <>
          <ListItemIcon>
            <RedoIcon />
          </ListItemIcon>
          <ListItemText>Retry - From failed task</ListItemText>
        </>
      ),
      handler: () => retryAction.mutate(),
    });
  }

  // RETRY (from task) button using latest definitions
  if (["FAILED", "TIMED_OUT", "TERMINATED"].includes(execution.status)) {
    options.push({
      label: (
        <>
          <ListItemIcon>
            <RedoIcon />
          </ListItemIcon>
          <ListItemText>
            Retry - From failed task (Latest Definitions)
          </ListItemText>
        </>
      ),
      handler: () => retryLatestDefinitionsAction.mutate(),
    });
  }

  // RETRY (failed subworkflow) button
  if (
    ["FAILED", "TIMED_OUT", "TERMINATED"].includes(execution.status) &&
    execution.tasks.find(
      (task) =>
        task.workflowTask.type === "SUB_WORKFLOW" && isFailedTask(task.status)
    )
  ) {
    options.push({
      label: (
        <>
          <ListItemIcon>
            <RedoIcon />
          </ListItemIcon>
          <ListItemText>Retry - Resume failed subworkflow</ListItemText>
        </>
      ),
      handler: () => retryResumeSubworkflowTasksAction.mutate(),
    });
  }

  // RERUN button

  // TERMINATE button
  if (["RUNNING", "FAILED", "TIMED_OUT", "PAUSED"].includes(execution.status)) {
    options.push({
      label: (
        <>
          <ListItemIcon className={classes.terminate}>
            <StopIcon />
          </ListItemIcon>
          <ListItemText className={classes.terminate}>Terminate</ListItemText>
        </>
      ),
      handler: () => terminateAction.mutate(),
    });

    options.push({
      label: (
        <>
          <ListItemIcon className={classes.terminate}>
            <StopIcon />
          </ListItemIcon>
          <ListItemText className={classes.terminate}>
            Terminate with Reason
          </ListItemText>
        </>
      ),
      handler: () => {
        const reason = window.prompt("Termination Reason", "");
        if (reason) terminateAction.mutate({ reason });
      },
    });
  }

  return (
    <DropdownButton
      options={options}
      disabled={!restartable}
      style={{ height: "32px", minWidth: "32px" }}
    >
      Actions
    </DropdownButton>
  );
}
